import { useEffect, useState } from 'react'
import { getRegionOptions, RegionOption } from '@/service/api/oem_admin/operators'
import { Loader2 } from 'lucide-react'

export interface RegionState {
  level: string // region | province | city | district
  region?: string
  province?: string
  city?: string
  district?: string
}

const LEVELS = [
  { value: 'region', label: '大区' },
  { value: 'province', label: '省' },
  { value: 'city', label: '市' },
  { value: 'district', label: '区县' },
]

const selectCls =
  'w-full h-9 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm px-2 focus:outline-none focus:ring-1 focus:ring-zinc-500'

/**
 * 区域四级联动选择器（大区→省→市→区县）。
 * 受控对象 RegionState：level 决定最终提交 region_id = state[level]。
 */
export function RegionCascader({
  value,
  onChange,
  hideLevel = false,
  hideRegion = false,
}: {
  value: RegionState
  onChange: (v: RegionState) => void
  hideLevel?: boolean
  /** 隐藏「大区」一层，省列表独立加载（注册页只选省/市/区县，大区由省反查） */
  hideRegion?: boolean
}) {
  const [regionOpts, setRegionOpts] = useState<RegionOption[]>([])
  const [provinceOpts, setProvinceOpts] = useState<RegionOption[]>([])
  const [cityOpts, setCityOpts] = useState<RegionOption[]>([])
  const [districtOpts, setDistrictOpts] = useState<RegionOption[]>([])
  const [loadingLevel, setLoadingLevel] = useState<string | null>(null)

  useEffect(() => {
    if (hideRegion) {
      getRegionOptions('province', '').then((r) => setProvinceOpts(r as RegionOption[]))
    } else {
      getRegionOptions('region', '').then((r) => setRegionOpts(r as RegionOption[]))
    }
  }, [hideRegion])

  const pick = async (
    lv: 'region' | 'province' | 'city' | 'district',
    val: string,
    next?: 'province' | 'city' | 'district'
  ) => {
    const nv: RegionState = { ...value, [lv]: val }
    if (lv === 'region') {
      nv.province = undefined
      nv.city = undefined
      nv.district = undefined
    } else if (lv === 'province') {
      nv.city = undefined
      nv.district = undefined
    } else if (lv === 'city') {
      nv.district = undefined
    }
    onChange(nv)
    if (next) {
      setLoadingLevel(next)
      try {
        const opts = (await getRegionOptions(next, val)) as RegionOption[]
        if (next === 'province') setProvinceOpts(opts)
        else if (next === 'city') setCityOpts(opts)
        else if (next === 'district') setDistrictOpts(opts)
      } finally {
        setLoadingLevel(null)
      }
    }
  }

  return (
    <div className="space-y-2">
      {!hideLevel && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400 w-12 flex-none">层级</span>
          <select
            className={selectCls}
            value={value.level}
            onChange={(e) => onChange({ ...value, level: e.target.value })}
          >
            {LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {!hideRegion && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400 w-12 flex-none">大区</span>
          <select
            className={selectCls}
            value={value.region || ''}
            onChange={(e) => pick('region', e.target.value, 'province')}
          >
            <option value="">请选择大区</option>
            {regionOpts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 w-12 flex-none">省</span>
        <select
          className={selectCls}
          disabled={!hideRegion && !value.region}
          value={value.province || ''}
          onChange={(e) => pick('province', e.target.value, 'city')}
        >
          <option value="">请选择省</option>
          {provinceOpts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 w-12 flex-none">市</span>
        <select
          className={selectCls}
          disabled={!value.province}
          value={value.city || ''}
          onChange={(e) => pick('city', e.target.value, 'district')}
        >
          <option value="">请选择市</option>
          {cityOpts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 w-12 flex-none">区县</span>
        <select
          className={selectCls}
          disabled={!value.city}
          value={value.district || ''}
          onChange={(e) => pick('district', e.target.value)}
        >
          <option value="">请选择区县</option>
          {districtOpts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {loadingLevel && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
      </div>
    </div>
  )
}
